import { useEffect, useEffectEvent, useLayoutEffect, useRef } from 'react';
import type { As400ExportGroup } from '@erp/rpc';

/**
 * A worksheet page. By worksheet, not pending-edit scope: a scope also names
 * the divisions in view, and those can differ from one worksheet to the next
 * (each greys out the divisions it has nothing in), so the scope a jump is
 * headed for isn't known until its worksheet is open.
 */
interface Place {
  group: As400ExportGroup;
  page: number;
}

function samePlace(a: Place | null, b: Place): boolean {
  return a !== null && a.group === b.group && a.page === b.page;
}

/** A cell to go to: the worksheet page it is on, and what to do there. */
export interface WorksheetCellJump extends Place {
  /**
   * Runs once the cell's page is on screen: select the cell (or open its
   * comments), or say it isn't there.
   */
  arrive: () => void;
}

/** Goes to a cell on any worksheet page — see {@link useWorksheetCellJump}. */
export type GoToWorksheetCell = (jump: WorksheetCellJump) => void;

/**
 * The review's open-then-select (GEN-2747): going to a cell on another
 * worksheet or page first opens it, then acts on the cell once that page has
 * loaded (as does a jump on the current page while it is still loading);
 * going anywhere else first drops it. One per grid, shared by everything that
 * jumps to a cell — the error list and the comments — so a later jump replaces
 * an earlier one rather than racing it.
 *
 * The returned function reads where the grid is when it is called, not when
 * it was handed out, so it may be called after an await (a comment's row is
 * looked up on the server first).
 */
export function useWorksheetCellJump({
  onOpen,
  group,
  page,
  isLoaded,
}: {
  /** Show `group`'s worksheet at `page` (the current one, or another). */
  onOpen: ((group: As400ExportGroup, page: number) => void) | undefined;
  /** The worksheet on screen; undefined where there are none to jump between. */
  group: As400ExportGroup | undefined;
  page: number;
  /** The current page's rows are on screen. */
  isLoaded: boolean;
}): GoToWorksheetCell {
  // A jump waiting on its page to load, with the page it was made from. Not
  // state: nothing renders it, it only acts on the grid once the page is in.
  const awaiting = useRef<{ jump: WorksheetCellJump; from: Place } | null>(
    null,
  );
  const current = useRef({ onOpen, group, page, isLoaded });
  useLayoutEffect(() => {
    current.current = { onOpen, group, page, isLoaded };
  });

  // Acting on the cell once its page is on screen syncs Handsontable (outside
  // React) with a jump asked for before the page existed. A page that is
  // neither the jump's nor the one it was made from means the author went
  // elsewhere: the jump is dropped, not sprung on a later visit.
  const arriveAwaited = useEffectEvent(() => {
    const queued = awaiting.current;
    if (!queued) return;
    const here = group ? { group, page } : null;
    if (!samePlace(here, queued.jump)) {
      if (!samePlace(here, queued.from)) awaiting.current = null;
      return;
    }
    if (!isLoaded) return;
    awaiting.current = null;
    queued.jump.arrive();
  });
  useEffect(() => {
    arriveAwaited();
  }, [isLoaded, group, page]);

  return (jump) => {
    const now = current.current;
    if (!now.onOpen || !now.group) return;
    const from = { group: now.group, page: now.page };
    if (samePlace(from, jump) && now.isLoaded) {
      awaiting.current = null;
      jump.arrive();
      return;
    }
    // Acted on once the page is on screen — this one, still loading, or the
    // one opened here.
    awaiting.current = { jump, from };
    if (!samePlace(from, jump)) now.onOpen(jump.group, jump.page);
  };
}
